import React from 'react';
import { Box, Typography, Button } from '@mui/material';

interface Question {
  id: number;
  userAnswer?: number;
  isMarkedForReview?: boolean;
  isVisited?: boolean;
}

interface QuestionNavigatorProps {
  questions: Question[];
  currentQuestionIndex: number;
  onQuestionSelect: (index: number) => void;
  lightenColor?: (color: string, amount: number) => string;
}

const QuestionNavigator: React.FC<QuestionNavigatorProps> = ({
  questions,
  currentQuestionIndex,
  onQuestionSelect,
  lightenColor = (color: string, amount: number) => color // Default fallback
}) => {
  return (
    <>
      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
        Question Navigator
      </Typography>
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: {
          xs: 'repeat(6, 1fr)',
          sm: 'repeat(6, 1fr)',
          md: 'repeat(5, 1fr)'
        },
        columnGap: 'clamp(2vw, 0.8rem)',
        rowGap: '0.75rem',
        mb: 3,
        justifyItems: 'center'
      }}>
        {questions.map((question, index) => {
          const isAnswered = question.userAnswer !== undefined;
          const isMarked = question.isMarkedForReview;
          const isVisited = question.isVisited;
          const isCurrent = index === currentQuestionIndex;
          
          return (
            <Button
              key={question.id}
              variant={isCurrent ? 'contained' : 'outlined'}
              size="small"
              onClick={() => onQuestionSelect(index)}
              sx={{
                minWidth: '40px',
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                p: 0,
                fontSize: '0.875rem',
                fontWeight: 600,
                border: isCurrent ? 'none' : '2px solid',
                borderColor: isAnswered 
                  ? '#4caf50' 
                  : isMarked 
                    ? '#ff9800' 
                    : isVisited 
                      ? '#ba68c8' 
                      : '#e0e0e0',
                backgroundColor: isCurrent 
                  ? '#1976d2' 
                  : isAnswered 
                    ? '#e8f5e8' 
                    : isMarked 
                      ? '#fff3e0' 
                      : isVisited 
                        ? '#f8e5ff' 
                        : 'transparent',
                color: isCurrent 
                  ? 'white' 
                  : isAnswered 
                    ? '#2e7d32' 
                    : isMarked 
                      ? '#f57c00' 
                      : isVisited 
                        ? '#8e24aa' 
                        : '#757575',
                '&:hover': {
                  backgroundColor: isCurrent 
                    ? '#1565c0' 
                    : isAnswered 
                      ? lightenColor('#4caf50', 0.1) 
                      : isMarked 
                        ? lightenColor('#ff9800', 0.1) 
                        : isVisited 
                          ? lightenColor('#ba68c8', 0.1) 
                          : '#f5f5f5',
                  borderColor: isCurrent 
                    ? '#1565c0' 
                    : isAnswered 
                      ? '#4caf50' 
                      : isMarked 
                        ? '#ff9800' 
                        : isVisited 
                          ? '#ba68c8' 
                          : '#e0e0e0'
                }
              }}
            >
              {index + 1}
            </Button>
          );
        })}
      </Box>
    </>
  );
};

export default QuestionNavigator;